// Vehicle catalog admin page — the pre-approved make/model/year reference table
// (Postgres `vehicle`) every driver_vehicle FKs into.
//
// Record browser, not a spreadsheet: editing happens in the side panel, where the
// driverCount context lives so retiring a model isn't a blind call. See NOTES.md.
//
// Its own "Vehicles" tab, separate from Fleet — Fleet is Hitch-owned vehicles,
// this is the catalog they and every driver vehicle reference.
//
// Search, filters, sorting and paging are ALL server-side (/admin/vehicles).
// Nothing here filters or sorts the loaded page client-side, because the page is
// 25 of N rows — sorting it locally would only order what you can already see.
import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  DataGrid,
  Alert,
  FilterSelect,
  Popover,
  Slide,
} from '../../ui/core';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import DashboardLayout from '../../layouts/DashoardLayout';
import {
  cardHeaderSx,
  filterPillSx,
  pillButtonSx,
  searchBarInputSx,
  floatSurfaceSx,
  flushPageSx,
  surfaceCardSx,
  softFill,
  hairline,
} from '../../ui/styles';
import { CloseIcon, ExpandMoreIcon } from '../../ui/icons';
import { EligiblePill, FIELDS, XlPill } from './bits';
import useVehicleCatalog, { ELIGIBILITY_OPTIONS, PAGE_SIZE, XL_OPTIONS } from './useVehicleCatalog';
import VehiclePanel from './VehiclePanel';

const FilterField = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

/**
 * The picked values of a multi-select filter, each removable. The pill itself
 * only says "N selected", so without these you can't see or drop one value
 * without reopening the dropdown.
 */
const SelectionChips = ({ values, onRemove }) =>
  values.length === 0 ? null : (
    <Stack direction="row" flexWrap="wrap" sx={{ gap: '6px', mt: '8px' }}>
      {values.map((value) => (
        <Chip
          key={String(value)}
          label={String(value)}
          size="small"
          onDelete={() => onRemove(value)}
          deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
          sx={{
            height: '24px',
            borderRadius: '8px',
            backgroundColor: softFill,
            fontSize: '12.5px',
            fontWeight: 500,
            '.MuiChip-deleteIcon': { color: 'text.secondary', '&:hover': { color: 'text.primary' } },
          }}
        />
      ))}
    </Stack>
  );

const VehicleCatalog = () => {
  const catalog = useVehicleCatalog();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  // `{}` means "new vehicle"; a row object means edit. Selection itself lives in
  // the URL so a panel stays open across a refresh.
  const [creating, setCreating] = useState(false);

  const selected = creating ? {} : catalog.rows.find((r) => r.id === catalog.vehicleId) || null;

  // The panel has to keep rendering while it slides out, by which point `selected`
  // is already null — so hold the last one it was opened with.
  const lastSelected = useRef(null);
  if (selected) lastSelected.current = selected;

  const closePanel = () => {
    setCreating(false);
    catalog.select('');
  };

  const columns = [
    ...FIELDS.map((f) => ({
      field: f.key,
      headerName: f.label,
      minWidth: f.width,
      flex: f.key === 'type' || f.key === 'model' ? 1.4 : 1,
      sortable: f.sortable,
      renderCell: ({ row }) => (
        <Typography sx={{ fontWeight: f.key === 'make' ? 600 : 500, whiteSpace: 'normal' }}>
          {row[f.key] == null || row[f.key] === '' ? '—' : row[f.key]}
        </Typography>
      ),
    })),
    {
      field: 'isXl',
      headerName: 'XL',
      minWidth: 80,
      sortable: true,
      renderCell: ({ row }) => <XlPill xl={row.isXl} />,
    },
    {
      field: 'isEligible',
      headerName: 'Status',
      minWidth: 120,
      sortable: true,
      renderCell: ({ row }) => <EligiblePill eligible={row.isEligible} />,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, backgroundColor: 'background.paper' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Box sx={cardHeaderSx}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em' }}>
                Vehicle Catalog
              </Typography>
              <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                {catalog.loading
                  ? 'Loading…'
                  : `${catalog.count} vehicle${catalog.count === 1 ? '' : 's'}`}
                {catalog.q ? ` matching “${catalog.q}”` : ''}
              </Typography>
            </Box>

            <TextField
              value={catalog.queryInput}
              onChange={(e) => catalog.setQueryInput(e.target.value)}
              placeholder="Search make, model or year"
              // searchBarInputSx pads .MuiInputBase-root; a bare outlined
              // TextField pads the inner input too, and the two stack into a
              // ~60px bar. Zero the inner one and match the 34px filter pills.
              sx={{
                ...searchBarInputSx,
                width: 260,
                '.MuiInputBase-root': {
                  ...searchBarInputSx['.MuiInputBase-root'],
                  height: '34px',
                  padding: '0 14px',
                },
                '.MuiInputBase-input': { padding: 0 },
              }}
            />

            <Badge badgeContent={catalog.activeFilterCount} color="primary">
              <Button
                endIcon={<ExpandMoreIcon />}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                sx={filterPillSx}
              >
                Filters
              </Button>
            </Badge>

            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: '8px', ...floatSurfaceSx } }}
            >
              <Stack spacing={1.75} sx={{ padding: '16px', minWidth: 280 }}>
                {/* Every filter reads the same way: heading above, pill below.
                    inlineLabel={false} keeps the pill from repeating the
                    heading once a value is picked. */}
                <FilterField label="Status">
                  <FilterSelect
                    label="Status"
                    inlineLabel={false}
                    searchable={false}
                    // 'all' is the cleared state, so it's the clear row rather
                    // than a pickable option — matching the other three.
                    clearLabel="All"
                    value={ELIGIBILITY_OPTIONS.find((o) => o.key === catalog.eligibility) || null}
                    options={ELIGIBILITY_OPTIONS}
                    getOptionLabel={(o) => o.label}
                    getOptionKey={(o) => o.key}
                    onChange={(o) => catalog.setEligibility(o ? o.key : 'all')}
                  />
                </FilterField>

                <FilterField label="XL">
                  <FilterSelect
                    label="XL"
                    inlineLabel={false}
                    searchable={false}
                    clearLabel="All"
                    value={XL_OPTIONS.find((o) => o.key === catalog.xl) || null}
                    options={XL_OPTIONS}
                    getOptionLabel={(o) => o.label}
                    getOptionKey={(o) => o.key}
                    onChange={(o) => catalog.setXl(o ? o.key : 'all')}
                  />
                </FilterField>

                {/* All three are multi-select: several values inside one filter
                    match any of them. Options cascade — models are scoped to the
                    selected makes, years to the selected models — so changing the
                    makes clears both below it. */}
                <FilterField label="Make">
                  <FilterSelect
                    multiple
                    label="Make"
                    inlineLabel={false}
                    placeholder="All makes"
                    clearLabel="All makes"
                    value={catalog.make}
                    options={catalog.options.makes}
                    onChange={catalog.setMakes}
                  />
                  <SelectionChips
                    values={catalog.make}
                    onRemove={(v) => catalog.setMakes(catalog.make.filter((m) => m !== v))}
                  />
                </FilterField>
                <FilterField label="Model">
                  <FilterSelect
                    multiple
                    label="Model"
                    inlineLabel={false}
                    placeholder="All models"
                    clearLabel="All models"
                    value={catalog.model}
                    options={catalog.options.models}
                    onChange={catalog.setModels}
                  />
                  <SelectionChips
                    values={catalog.model}
                    onRemove={(v) => catalog.setModels(catalog.model.filter((m) => m !== v))}
                  />
                </FilterField>
                <FilterField label="Year">
                  <FilterSelect
                    multiple
                    label="Year"
                    inlineLabel={false}
                    placeholder="All years"
                    clearLabel="All years"
                    // Options come back as numbers, the URL holds strings — compare
                    // as strings so a selected year shows as ticked and as a chip.
                    value={catalog.year}
                    options={catalog.options.years.map(String)}
                    onChange={catalog.setYears}
                  />
                  <SelectionChips
                    values={catalog.year}
                    onRemove={(v) => catalog.setYears(catalog.year.filter((y) => y !== v))}
                  />
                </FilterField>

                {catalog.activeFilterCount > 0 && (
                  <Button
                    onClick={catalog.clearFilters}
                    sx={{
                      ...filterPillSx,
                      alignSelf: 'flex-start',
                      border: 'none',
                      color: 'error.main',
                      backgroundColor: (t) => alpha(t.palette.error.main, 0.12),
                      '&:hover': { backgroundColor: (t) => alpha(t.palette.error.main, 0.2) },
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </Stack>
            </Popover>

            <Button
              onClick={() => {
                catalog.select('');
                setCreating(true);
              }}
              sx={pillButtonSx}
            >
              + Add Vehicle
            </Button>
          </Stack>
          {catalog.error && (
            <Alert severity="error" onClose={() => catalog.setError(null)} sx={{ mt: 1.5 }}>
              {catalog.error}
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            borderTop: (t) => `1px solid ${hairline(t)}`,
          }}
        >
          <DataGrid
            rows={catalog.rows}
            columns={columns}
            onRowClick={({ row }) => {
              setCreating(false);
              catalog.select(row.id);
            }}
            getRowClassName={({ row }) => (row.isEligible ? '' : 'vehicle--retired')}
            loading={catalog.loading}
            pageSize={PAGE_SIZE}
            paginationMode="server"
            rowCount={catalog.count}
            page={catalog.page}
            onPageChange={catalog.setPage}
            sortBy={catalog.sortBy}
            sortDir={catalog.sortDir}
            onSortChange={catalog.toggleSort}
            // Rows grow to fit instead of clipping. Columns keep their widths, so
            // opening the panel wraps a long "Mazda CX-5 Grand Touring" onto a
            // second line rather than ellipsing it. The shared DataGrid clips by
            // default (whiteSpace: nowrap, fixed 60px rows) and every other table
            // still does — this is a per-page opt-out.
            getRowHeight={() => 'auto'}
            sx={{
              '.vehicle--retired': { opacity: 0.55 },
              '.DataGrid-cell': {
                whiteSpace: 'normal',
                overflow: 'visible',
                // Keeps single-line rows at the density of every other table;
                // only a wrapped cell makes its row taller.
                minHeight: '60px',
                py: 1,
              },
            }}
          />
        </Box>
      </Box>

      {/* Slides in from the right edge and back out. The wrapper takes the
          transform because VehiclePanel doesn't forward a ref, and it keeps its
          360px of layout width until the exit finishes so the grid doesn't
          snap wider mid-slide. */}
      <Slide direction="left" in={Boolean(selected)} mountOnEnter unmountOnExit>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          {lastSelected.current && (
            <VehiclePanel vehicle={lastSelected.current} catalog={catalog} onClose={closePanel} />
          )}
        </Box>
      </Slide>
    </Box>
  );
};

const Vehicles = () => (
  <DashboardLayout flush title="Vehicles">
    <Box sx={flushPageSx}>
      <Paper elevation={0} sx={{ ...surfaceCardSx, flex: 1, minWidth: 0 }}>
        <VehicleCatalog />
      </Paper>
    </Box>
  </DashboardLayout>
);

export default Vehicles;
